import React, { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { ChevronDown } from 'lucide-react';
import { useTranslation } from '../context/TranslationContext';
import { useFrontendAuth } from '../hooks/useAuth';
import { ShopUserInfo } from '../types/auth';

const LanguageSwitcher: React.FC = () => {
    const { changeLanguage, currentLang, languages } = useTranslation();
    const { isAuthenticated, isAuthReady } = useFrontendAuth();
    const queryClient = useQueryClient();
    const [open, setOpen] = useState(false);
    const rootRef = useRef<HTMLDivElement>(null);

    const handleChange = async (lang: string) => {
        const persist = isAuthReady && isAuthenticated;
        await changeLanguage(lang, { persist });
        if (persist) {
            queryClient.setQueryData<ShopUserInfo | null>(['user'], (prev) =>
                prev ? { ...prev, preferredLanguage: lang } : prev
            );
        }
        setOpen(false);
    };

    useEffect(() => {
        if (!open) return;
        const onDoc = (e: MouseEvent) => {
            if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
        };
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setOpen(false);
        };
        document.addEventListener('mousedown', onDoc);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('mousedown', onDoc);
            document.removeEventListener('keydown', onKey);
        };
    }, [open]);

    const current = languages.find((l) => l.code === currentLang);
    const triggerLabel = current
        ? `${current.flagEmoji} ${current.nativeName || current.name}`
        : currentLang || '…';

    return (
        <div ref={rootRef} className="relative">
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                aria-haspopup="listbox"
                aria-expanded={open}
                style={{ fontFamily: '"Twemoji Country Flags", sans-serif' }}
                className="inline-flex max-w-[42vw] items-center gap-1 truncate rounded-full bg-[var(--accent-color)] px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-[color-mix(in_srgb,var(--accent-color),black_20%)] sm:max-w-none sm:px-4 sm:py-1.5 sm:text-sm"
            >
                <span className="truncate">{triggerLabel}</span>
                <ChevronDown
                    className={`h-3.5 w-3.5 shrink-0 opacity-70 transition ${open ? 'rotate-180' : ''}`}
                    aria-hidden
                />
            </button>

            {open && (
                <ul
                    role="listbox"
                    className="absolute right-0 z-50 mt-2 min-w-[10rem] overflow-hidden rounded-2xl border border-[var(--accent-color)]/40 bg-[var(--bg-color)] py-1 shadow-xl"
                    style={{ fontFamily: '"Twemoji Country Flags", sans-serif' }}
                >
                    {languages.length > 0 ? (
                        languages.map((lang) => {
                            const selected = lang.code === currentLang;
                            return (
                                <li key={lang.code} role="option" aria-selected={selected}>
                                    <button
                                        type="button"
                                        onClick={() => void handleChange(lang.code)}
                                        className={`flex w-full items-center gap-2 px-4 py-2.5 text-left text-sm transition hover:bg-[color-mix(in_srgb,var(--accent-color),transparent_55%)] ${
                                            selected
                                                ? 'bg-[color-mix(in_srgb,var(--accent-color),transparent_40%)] font-semibold text-[var(--text-color)]'
                                                : 'text-[var(--text-color)]'
                                        }`}
                                    >
                                        <span>{lang.flagEmoji}</span>
                                        <span className="truncate">{lang.nativeName || lang.name}</span>
                                    </button>
                                </li>
                            );
                        })
                    ) : (
                        <li className="px-4 py-2 text-sm opacity-60">Loading languages...</li>
                    )}
                </ul>
            )}
        </div>
    );
};

export default LanguageSwitcher;
